import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { HttpError } from "@/api/client";
import { registerRequest } from "@/api/auth";
import { useAuth } from "@/auth/AuthProvider";

function sanitizeNext(value: string | null): string {
  if (!value) return "/";
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}

export function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = sanitizeNext(params.get("next"));

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState<"pilot" | "instructor">(
    "pilot",
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await registerRequest(username, password, accountType);
      await login(username, password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message);
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="auth-shell">
      <article className="auth-card">
        <p className="eyebrow">Register</p>
        <h1>Create your flight workspace</h1>
        <p className="lede">
          Pick a username, a password, and your role. Admin accounts are created
          only by existing admins inside the app.
        </p>

        {error && (
          <div className="callout danger">
            <strong>Registration failed</strong>
            <p>{error}</p>
          </div>
        )}

        <form className="upload-form" onSubmit={handleSubmit}>
          <label className="file-input">
            <span>Username</span>
            <input
              type="text"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="min. 3 characters"
              required
              minLength={3}
              autoComplete="username"
            />
          </label>
          <label className="file-input">
            <span>Password</span>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min. 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </label>
          <label className="file-input">
            <span>Account type</span>
            <select
              value={accountType}
              onChange={(e) =>
                setAccountType(e.target.value as "pilot" | "instructor")
              }
            >
              <option value="pilot">Pilot</option>
              <option value="instructor">Instructor</option>
            </select>
          </label>
          <button className="button primary" type="submit" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="form-note">
          Already have an account?{" "}
          <Link
            className="text-link"
            to={`/login?next=${encodeURIComponent(nextPath)}`}
          >
            Sign in
          </Link>
        </p>
      </article>
    </section>
  );
}
